import React, { useEffect, useMemo, useState } from 'react';
import { FaFileExcel, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';
import { SearchableSelect, SelectWithSearch, type SelectOption } from '../../utils/select-with-search';
import { dateRangeInputBounds, firstDateRangeError } from '../../utils/date-range.utils';

interface UserFeedbackRow {
  [key: string]: any;
}

interface UserFeedbackFilters {
  search: string;
  project_id: string;
  department_id: string;
  activity_id: string;
  state_circle_id: string;
  district_id: string;
  site_engineer_userid: string;
  survey_workflow_status_list: string;
  association_type: string;
  site_id: string;
  site_name: string;
  claim_status_list: string;
  travel_mode: string;
  terrain_type: string;
  is_project_claim_submitted: string;
  billing_status_list: string;
  wcc_status: string;
  invoice_status: string;
  is_billing_done: string;
  report_submitted: string;
  is_po_received: string;
  client_allocation_date_from: string;
  client_allocation_date_to: string;
  site_engineer_assign_date_from: string;
  site_engineer_assign_date_to: string;
  site_engineer_work_date_from: string;
  site_engineer_work_date_to: string;
  allocation_date_from: string;
  allocation_date_to: string;
  survey_date_from: string;
  survey_date_to: string;
  workflow_action_type: string;
  workflow_to_status: string;
  workflow_performed_at_from: string;
  workflow_performed_at_to: string;
  sort_by: string;
  sort_order: 'ASC' | 'DESC';
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const DEFAULT_FILTERS: UserFeedbackFilters = {
  search: '',
  project_id: '',
  department_id: '',
  activity_id: '',
  state_circle_id: '',
  district_id: '',
  site_engineer_userid: '',
  survey_workflow_status_list: '',
  association_type: '',
  site_id: '',
  site_name: '',
  claim_status_list: '',
  travel_mode: '',
  terrain_type: '',
  is_project_claim_submitted: '',
  billing_status_list: '',
  wcc_status: '',
  invoice_status: '',
  is_billing_done: '',
  report_submitted: '',
  is_po_received: '',
  client_allocation_date_from: '',
  client_allocation_date_to: '',
  site_engineer_assign_date_from: '',
  site_engineer_assign_date_to: '',
  site_engineer_work_date_from: '',
  site_engineer_work_date_to: '',
  allocation_date_from: '',
  allocation_date_to: '',
  survey_date_from: '',
  survey_date_to: '',
  workflow_action_type: '',
  workflow_to_status: '',
  workflow_performed_at_from: '',
  workflow_performed_at_to: '',
  sort_by: 'created_at',
  sort_order: 'DESC',
};

const FEEDBACK_COLUMNS = [
  'TA Remarks',
  'TA Quality Feedback',
  'TA Rating',
  'MIS Remarks',
  'MIS Quality Feedback',
  'Report Designer Remarks',
  'Report Designer Quality Feedback',
  'Report Designer Rejection Reason',
  'Validator Remarks',
  'Validator Quality Feedback',
  'Validator Rating',
] as const;

type DropdownOption = {
  value: string | number;
  label: string;
};

const WORKFLOW_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'site_created', label: 'Site Created' },
  { value: 'engineer_assigned', label: 'Engineer Assigned' },
  { value: 'field_work_in_progress', label: 'Field Work In Progress' },
  { value: 'data_uploaded', label: 'Data Uploaded' },
  { value: 'field_work_completed', label: 'Field Work Completed' },
  { value: 'ta_review_pending', label: 'TA Review Pending' },
  { value: 'ta_review_completed', label: 'TA Review Completed' },
  { value: 'ta_rejected', label: 'TA Rejected' },
  { value: 'mis_report_assign_pending', label: 'MIS Report Assign Pending' },
  { value: 'report_maker_assigned', label: 'Report Maker Assigned' },
  { value: 'report_in_progress', label: 'Report In Progress' },
  { value: 'report_completed', label: 'Report Completed' },
  { value: 'report_maker_rejected', label: 'Report Maker Rejected' },
  { value: 'validator_assigned', label: 'Validator Assigned' },
  { value: 'validation_in_progress', label: 'Validation In Progress' },
  { value: 'validation_completed', label: 'Validation Completed' },
  { value: 'validation_rejected', label: 'Validation Rejected' },
  { value: 'report_finalized', label: 'Report Finalized' },
  { value: 'report_sent_to_client', label: 'Report Sent To Client' },
];

const CLAIM_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const BILLING_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'wcc_raised', label: 'WCC Raised' },
  { value: 'wcc_approved', label: 'WCC Approved' },
  { value: 'invoice_raised', label: 'Invoice Raised' },
  { value: 'invoice_submitted', label: 'Invoice Submitted' },
  { value: 'paid', label: 'Paid' },
];

const WCC_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const INVOICE_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];

const WORKFLOW_ACTION_OPTIONS: DropdownOption[] = [
  { value: 'assignment', label: 'Assignment' },
  { value: 'status_change', label: 'Status Change' },
  { value: 'approval', label: 'Approval' },
  { value: 'data_upload', label: 'Data Upload' },
  { value: 'location_verification', label: 'Location Verification' },
];

const NUMERIC_FILTER_KEYS: Array<keyof UserFeedbackFilters> = [
  'project_id',
  'department_id',
  'activity_id',
  'state_circle_id',
  'district_id',
  'site_engineer_userid',
];

const BOOLEAN_FILTER_KEYS: Array<keyof UserFeedbackFilters> = [
  'is_project_claim_submitted',
  'is_billing_done',
  'report_submitted',
  'is_po_received',
];

const withFallback = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'NA';
  return String(value);
};

const toReadableLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const parseMultiValue = (value: string, options: DropdownOption[]) => {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((itemValue) => {
      const matched = options.find((option) => String(option.value) === itemValue);
      return matched || { value: itemValue, label: toReadableLabel(itemValue) };
    });
};

const pickFirst = (row: UserFeedbackRow, keys: string[]) => {
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
  }
  return '';
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]';

const flattenRow = (value: unknown, prefix = '', out: Record<string, unknown> = {}) => {
  if (!isPlainObject(value)) return out;
  Object.entries(value).forEach(([rawKey, rawValue]) => {
    const key = rawKey.trim();
    if (!key || key.toLowerCase() === 'id') return;
    const path = prefix ? `${prefix}.${key}` : key;
    if (rawValue === null || rawValue === undefined || typeof rawValue !== 'object') {
      out[path] = rawValue;
      return;
    }
    if (Array.isArray(rawValue)) {
      if (rawValue.length === 0) {
        out[path] = null;
      } else if (rawValue.some((item) => isPlainObject(item) || Array.isArray(item))) {
        out[path] = JSON.stringify(rawValue);
      } else {
        out[path] = rawValue.join(', ');
      }
      return;
    }
    flattenRow(rawValue, path, out);
  });
  return out;
};

const resolveFromFlat = (flat: Record<string, unknown>, paths: string[]) => {
  for (const path of paths) {
    const value = flat[path];
    if (value !== null && value !== undefined && value !== '') return withFallback(value);
  }
  return 'NA';
};

const buildFeedbackRow = (row: UserFeedbackRow): Record<string, string | number> => {
  const flat = flattenRow(row);
  return {
    'TA Remarks': resolveFromFlat(flat, ['ta_remarks', 'taRemarks', 'ta_review_remarks', 'ta_review.remarks']),
    'TA Quality Feedback': resolveFromFlat(flat, ['ta_se_quality_feedback', 'ta_quality_feedback', 'taQualityFeedback', 'ta_review.quality_feedback']),
    'TA Rating': resolveFromFlat(flat, ['ta_rating', 'taRating', 'ta_review.rating']),
    'MIS Remarks': resolveFromFlat(flat, ['mis_remarks', 'misRemarks', 'mis_review.remarks']),
    'MIS Quality Feedback': resolveFromFlat(flat, ['mis_validator_qa_feedback', 'mis_quality_feedback', 'misQualityFeedback', 'mis_review.quality_feedback']),
    'Report Designer Remarks': resolveFromFlat(flat, ['report_designer_remarks', 'report_maker_remarks', 'reportDesignerRemarks', 'report_designer_review.remarks', 'report_maker_review.remarks']),
    'Report Designer Quality Feedback': resolveFromFlat(flat, ['report_designer_quality_feedback', 'report_maker_quality_feedback', 'reportDesignerQualityFeedback', 'report_designer_review.quality_feedback', 'report_maker_review.quality_feedback']),
    'Report Designer Rejection Reason': resolveFromFlat(flat, ['report_designer_rejection_reason', 'report_maker_rejection_reason', 'reportDesignerRejectionReason', 'report_designer_review.rejection_reason', 'report_maker_review.rejection_reason']),
    'Validator Remarks': resolveFromFlat(flat, ['validator_remarks', 'validatorRemarks', 'validator_review.remarks']),
    'Validator Quality Feedback': resolveFromFlat(flat, ['validator_report_designer_qa_feedback', 'validator_quality_feedback', 'validatorQualityFeedback', 'validator_review.quality_feedback']),
    'Validator Rating': resolveFromFlat(flat, ['validator_rating', 'validatorRating', 'validator_review.rating']),
  };
};

interface FilterFormProps {
  filters: UserFeedbackFilters;
  onChange: (key: keyof UserFeedbackFilters, value: string) => void;
  onApply: () => void;
  onReset: () => void;
}

const FilterForm: React.FC<FilterFormProps> = ({ filters, onChange, onApply, onReset }) => {
  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null);
  const [selectedEngineer, setSelectedEngineer] = useState<SelectOption | null>(null);
  const [selectedStateCircle, setSelectedStateCircle] = useState<SelectOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>(null);

  const surveyWorkflowStatusValues = useMemo(() => parseMultiValue(filters.survey_workflow_status_list, WORKFLOW_STATUS_OPTIONS), [filters.survey_workflow_status_list]);
  const claimStatusValues = useMemo(() => parseMultiValue(filters.claim_status_list, CLAIM_STATUS_OPTIONS), [filters.claim_status_list]);
  const billingStatusValues = useMemo(() => parseMultiValue(filters.billing_status_list, BILLING_STATUS_OPTIONS), [filters.billing_status_list]);

  const selectedStateCircleId = selectedStateCircle ? Number(selectedStateCircle.value) : filters.state_circle_id ? Number(filters.state_circle_id) : null;

  useEffect(() => { if (!filters.project_id) return setSelectedProject(null); setSelectedProject((prev) => prev && String(prev.value) === filters.project_id ? prev : ({ id: Number(filters.project_id), value: Number(filters.project_id), label: `Project #${filters.project_id}` })); }, [filters.project_id]);
  useEffect(() => { if (!filters.department_id) return setSelectedDepartment(null); setSelectedDepartment((prev) => prev && String(prev.value) === filters.department_id ? prev : ({ id: Number(filters.department_id), value: Number(filters.department_id), label: `Department #${filters.department_id}` })); }, [filters.department_id]);
  useEffect(() => { if (!filters.activity_id) return setSelectedActivity(null); setSelectedActivity((prev) => prev && String(prev.value) === filters.activity_id ? prev : ({ id: Number(filters.activity_id), value: Number(filters.activity_id), label: `Activity #${filters.activity_id}` })); }, [filters.activity_id]);
  useEffect(() => { if (!filters.site_engineer_userid) return setSelectedEngineer(null); setSelectedEngineer((prev) => prev && String(prev.value) === filters.site_engineer_userid ? prev : ({ id: Number(filters.site_engineer_userid), value: Number(filters.site_engineer_userid), label: `User #${filters.site_engineer_userid}` })); }, [filters.site_engineer_userid]);
  useEffect(() => { if (!filters.state_circle_id) return setSelectedStateCircle(null); setSelectedStateCircle((prev) => prev && String(prev.value) === filters.state_circle_id ? prev : ({ id: Number(filters.state_circle_id), value: Number(filters.state_circle_id), label: `State Circle #${filters.state_circle_id}` })); }, [filters.state_circle_id]);
  useEffect(() => { if (!filters.district_id) return setSelectedDistrict(null); setSelectedDistrict((prev) => prev && String(prev.value) === filters.district_id ? prev : ({ id: Number(filters.district_id), value: Number(filters.district_id), label: `District #${filters.district_id}` })); }, [filters.district_id]);

  const renderTextInput = (key: keyof UserFeedbackFilters, label: string, placeholder = '') => (
    <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">{label}</span><input type="text" value={filters[key]} onChange={(e) => onChange(key, e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></label>
  );
  // Every date filter is one end of a `<name>_from` / `<name>_to` pair; the
  // picker is bounded by the other end of the same pair.
  const renderDateInput = (key: keyof UserFeedbackFilters, label: string) => {
    const pair = key.replace(/_(from|to)$/, '');
    const bounds = dateRangeInputBounds(filters[`${pair}_from` as keyof UserFeedbackFilters], filters[`${pair}_to` as keyof UserFeedbackFilters]);
    return (
    <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">{label}</span><input type="date" value={filters[key]} onChange={(e) => onChange(key, e.target.value)} {...(key.endsWith('_from') ? bounds.from : bounds.to)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></label>
    );
  };
  const renderSelect = (key: keyof UserFeedbackFilters, label: string, options: DropdownOption[]) => (
    <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">{label}</span><select value={filters[key]} onChange={(e) => onChange(key, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
  );
  const renderMultiSelect = (key: keyof UserFeedbackFilters, label: string, value: DropdownOption[], options: DropdownOption[]) => (
    <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">{label}</span><SearchableSelect value={value} onChange={(selected: DropdownOption[]) => onChange(key, (selected || []).map((item) => String(item.value)).join(','))} options={options} placeholder={`Select ${label.toLowerCase()}`} isMulti closeOnOutsideClick={false} /></label>
  );

  return (
    <div className="bg-white shadow-sm rounded border border-gray-200">
      <div className="flex items-center justify-between p-4 border-b border-gray-200"><div><h2 className="text-lg font-semibold text-gray-900">User Feedback Report Filters</h2><p className="text-sm text-gray-600">Apply filters, sorting, and search before loading feedback fields.</p></div></div>
      <div className="p-4 space-y-6">
        <section><h3 className="text-sm font-semibold text-gray-800 mb-3">Search and Filters</h3><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {renderTextInput('search', 'Search', 'airtel')}
          {renderTextInput('site_id', 'Site', 'SIT')}
          {renderTextInput('site_name', 'Site Name', 'Tower')}
          <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">Project</span><SelectWithSearch placeholder="Search project" value={selectedProject} onChange={(option) => { setSelectedProject(option); onChange('project_id', option ? String(option.value) : ''); }} apiEndpoint="/reports/site/projects" dataKey="projects" labelKey="project_name" valueKey="id" searchParam="search" className="w-full" /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">Department / Project Group</span><SelectWithSearch placeholder="Search department" value={selectedDepartment} onChange={(option) => { setSelectedDepartment(option); onChange('department_id', option ? String(option.value) : ''); }} apiEndpoint="/departments/list" dataKey="departments" labelKey="name" valueKey="id" searchParam="search" extraParams={{ assigned_only: true }} className="w-full" /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">Activity</span><SelectWithSearch placeholder="Search activity" value={selectedActivity} onChange={(option) => { setSelectedActivity(option); onChange('activity_id', option ? String(option.value) : ''); }} apiEndpoint="/activities/list" dataKey="activities" labelKey="activity_name" valueKey="id" searchParam="search" className="w-full" /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">State Circle</span><SelectWithSearch placeholder="Search state circle" value={selectedStateCircle} onChange={(option) => { setSelectedStateCircle(option); setSelectedDistrict(null); onChange('state_circle_id', option ? String(option.value) : ''); onChange('district_id', ''); }} apiEndpoint="/state-circle/list" dataKey="states" labelKey="state_name" valueKey="id" searchParam="search" className="w-full" /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">District</span><SelectWithSearch placeholder="Search district" value={selectedDistrict} onChange={(option) => { setSelectedDistrict(option); onChange('district_id', option ? String(option.value) : ''); }} apiEndpoint="/state-circle" dataKey="districts" labelKey="name" valueKey="id" searchParam="search" dependencyId={selectedStateCircleId} className="w-full" /></label>
          <label className="block"><span className="block text-xs font-medium text-gray-700 mb-1">Site Engineer</span><SelectWithSearch placeholder="Search engineer" value={selectedEngineer} onChange={(option) => { setSelectedEngineer(option); onChange('site_engineer_userid', option ? String(option.value) : ''); }} apiEndpoint="/users/list" dataKey="users" labelKey="first_name" valueKey="id" searchParam="search" displayFormat={(item: any) => { const fullName = [item.first_name, item.last_name].filter(Boolean).join(' ').trim(); return `${fullName || item.name || `User #${item.id}`}${item.employee_code ? ` (${item.employee_code})` : ''}`; }} className="w-full" /></label>
          {renderMultiSelect('survey_workflow_status_list', 'Survey Workflow Status List', surveyWorkflowStatusValues, WORKFLOW_STATUS_OPTIONS)}
          {renderMultiSelect('claim_status_list', 'Claim Status List', claimStatusValues, CLAIM_STATUS_OPTIONS)}
          {renderMultiSelect('billing_status_list', 'Billing Status List', billingStatusValues, BILLING_STATUS_OPTIONS)}
          {renderSelect('workflow_to_status', 'Workflow To Status', [{ value: '', label: 'All' }, ...WORKFLOW_STATUS_OPTIONS])}
        </div></section>
        <section><h3 className="text-sm font-semibold text-gray-800 mb-3">Status and Flags</h3><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {renderSelect('association_type', 'Association Type', [{ value: '', label: 'All' }, { value: 'inhouse', label: 'OnRole' }, { value: 'vendor', label: 'OffRole' }])}
          {renderSelect('travel_mode', 'Travel Mode', [{ value: '', label: 'All' }, { value: 'self', label: 'Self' }, { value: 'cab', label: 'Cab' }])}
          {renderSelect('terrain_type', 'Terrain Type', [{ value: '', label: 'All' }, { value: 'plain', label: 'Plain' }, { value: 'hills', label: 'Hills' }])}
          {renderSelect('wcc_status', 'WCC Status', [{ value: '', label: 'All' }, ...WCC_STATUS_OPTIONS])}
          {renderSelect('invoice_status', 'Invoice Status', [{ value: '', label: 'All' }, ...INVOICE_STATUS_OPTIONS])}
          {renderSelect('workflow_action_type', 'Workflow Action Type', [{ value: '', label: 'All' }, ...WORKFLOW_ACTION_OPTIONS])}
          {renderSelect('is_project_claim_submitted', 'Project Claim Submitted', [{ value: '', label: 'All' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }])}
          {renderSelect('is_billing_done', 'Billing Done', [{ value: '', label: 'All' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }])}
          {renderSelect('report_submitted', 'Report Submitted', [{ value: '', label: 'All' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }])}
          {renderSelect('is_po_received', 'PO Received', [{ value: '', label: 'All' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }])}
        </div></section>
        <section><h3 className="text-sm font-semibold text-gray-800 mb-3">Date Ranges</h3><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {renderDateInput('client_allocation_date_from', 'Client Allocation Date From')}
          {renderDateInput('client_allocation_date_to', 'Client Allocation Date To')}
          {renderDateInput('site_engineer_assign_date_from', 'Site Engineer Assign Date From')}
          {renderDateInput('site_engineer_assign_date_to', 'Site Engineer Assign Date To')}
          {renderDateInput('site_engineer_work_date_from', 'Site Engineer Work Date From')}
          {renderDateInput('site_engineer_work_date_to', 'Site Engineer Work Date To')}
          {renderDateInput('allocation_date_from', 'Allocation Date From')}
          {renderDateInput('allocation_date_to', 'Allocation Date To')}
          {renderDateInput('survey_date_from', 'Survey Date From')}
          {renderDateInput('survey_date_to', 'Survey Date To')}
          {renderDateInput('workflow_performed_at_from', 'Workflow Performed At From')}
          {renderDateInput('workflow_performed_at_to', 'Workflow Performed At To')}
        </div></section>
        <section><h3 className="text-sm font-semibold text-gray-800 mb-3">Sorting</h3><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {renderSelect('sort_by', 'Sort By', [{ value: 'created_at', label: 'created_at' }, { value: 'allocation_date', label: 'allocation_date' }, { value: 'survey_date', label: 'survey_date' }, { value: 'site_name', label: 'site_name' }, { value: 'site_id', label: 'site_id' }, { value: 'client_allocation_date', label: 'client_allocation_date' }, { value: 'site_engineer_assign_date', label: 'site_engineer_assign_date' }, { value: 'site_engineer_work_date', label: 'site_engineer_work_date' }])}
          {renderSelect('sort_order', 'Sort Order', [{ value: 'DESC', label: 'DESC' }, { value: 'ASC', label: 'ASC' }])}
        </div></section>
      </div>
      <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
        <button onClick={onReset} className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">Reset</button>
        <button onClick={onApply} className="px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900">Apply Filters</button>
      </div>
    </div>
  );
};

const UserFeedbackReport: React.FC = () => {
  const [rows, setRows] = useState<UserFeedbackRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>({
    total_items: 0,
    current_page: 1,
    total_pages: 1,
    items_per_page: 50,
    has_next_page: false,
    has_previous_page: false,
  });
  const [draftFilters, setDraftFilters] = useState<UserFeedbackFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<UserFeedbackFilters>(DEFAULT_FILTERS);
  const [showFilterForm, setShowFilterForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exportData, setExportData] = useState<Record<string, string | number>[]>([]);

  const activeFiltersCount = useMemo(() => {
    return Object.entries(appliedFilters).filter(([key, value]) => {
      if (!value) return false;
      if (key === 'sort_by' && value === DEFAULT_FILTERS.sort_by) return false;
      if (key === 'sort_order' && value === DEFAULT_FILTERS.sort_order) return false;
      return true;
    }).length;
  }, [appliedFilters]);

  const buildParams = (page: number, limit: number, filters: UserFeedbackFilters) => {
    const params: Record<string, string | number> = { page, limit };
    (Object.keys(filters) as Array<keyof UserFeedbackFilters>).forEach((key) => {
      const value = filters[key];
      if (!value) return;
      if (NUMERIC_FILTER_KEYS.includes(key)) {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) params[key] = parsed;
        return;
      }
      if (BOOLEAN_FILTER_KEYS.includes(key)) {
        (params as Record<string, string | number | boolean>)[key] = value === 'true';
        return;
      }
      params[key] = value;
    });
    return params;
  };

  const fetchRows = async (targetPage = 1, targetLimit = pagination.items_per_page, filters: UserFeedbackFilters = appliedFilters) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/user-feedback', {
        params: buildParams(targetPage, targetLimit, filters),
      });

      if (response.data.success) {
        const payload = response.data.data || {};
        const data: UserFeedbackRow[] = payload.data || [];
        const p = payload.pagination || {};
        const total = Number(p.total ?? p.total_items ?? p.total_count) || data.length;
        const currentPage = Number(p.current_page ?? p.page) || targetPage;
        const perPage = Number(p.per_page ?? p.items_per_page ?? p.limit) || targetLimit;
        const totalPages = Number(p.total_pages) || Math.max(1, Math.ceil(total / perPage));

        setRows(data);
        setPagination({
          total_items: total,
          current_page: currentPage,
          total_pages: totalPages,
          items_per_page: perPage,
          has_next_page: Boolean(p.has_next_page ?? currentPage < totalPages),
          has_previous_page: Boolean(p.has_prev_page ?? currentPage > 1),
        });
        setHasFetched(true);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch user feedback report');
    } finally {
      setLoading(false);
    }
  };

  const fetchExportData = async () => {
    try {
      setExporting(true);
      const pageSize = 100;
      let currentPage = 1;
      let totalPages = 1;
      const allRows: UserFeedbackRow[] = [];

      do {
        const response = await axiosInstance.get('/reports/user-feedback', {
          params: buildParams(currentPage, pageSize, appliedFilters),
        });
        if (!response.data.success) break;
        const payload = response.data.data || {};
        const data: UserFeedbackRow[] = payload.data || [];
        const p = payload.pagination || {};
        allRows.push(...data);
        totalPages =
          Number(p.total_pages) ||
          Math.max(1, Math.ceil((Number(p.total ?? p.total_items ?? p.total_count) || allRows.length) / pageSize));
        currentPage += 1;
      } while (currentPage <= totalPages);

      setExportData(allRows.map((row) => buildFeedbackRow(row)));
      setShowExcelModal(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to prepare user feedback export');
    } finally {
      setExporting(false);
    }
  };

  const paginationAdapter: PaginationType = useMemo(() => ({
    offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
    limit: pagination.items_per_page,
    page: pagination.current_page,
    total_pages: pagination.total_pages,
    total_count: pagination.total_items,
    has_next_page: pagination.has_next_page,
    has_prev_page: pagination.has_previous_page,
    next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
    prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null,
  }), [pagination]);

  const handleFilterChange = (key: keyof UserFeedbackFilters, value: string) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const dateError = firstDateRangeError([
      [draftFilters.client_allocation_date_from, draftFilters.client_allocation_date_to, 'Client Allocation Date'],
      [draftFilters.site_engineer_assign_date_from, draftFilters.site_engineer_assign_date_to, 'Site Engineer Assign Date'],
      [draftFilters.site_engineer_work_date_from, draftFilters.site_engineer_work_date_to, 'Site Engineer Work Date'],
      [draftFilters.allocation_date_from, draftFilters.allocation_date_to, 'Allocation Date'],
      [draftFilters.survey_date_from, draftFilters.survey_date_to, 'Survey Date'],
      [draftFilters.workflow_performed_at_from, draftFilters.workflow_performed_at_to, 'Workflow Performed At'],
    ]);
    if (dateError) {
      toast.error(dateError);
      return;
    }
    const normalized: UserFeedbackFilters = {
      ...draftFilters,
      sort_order: draftFilters.sort_order === 'ASC' ? 'ASC' : 'DESC',
      sort_by: draftFilters.sort_by || DEFAULT_FILTERS.sort_by,
    };
    setAppliedFilters(normalized);
    setShowFilterForm(false);
    fetchRows(1, pagination.items_per_page, normalized);
  };

  const handleResetDraftFilters = () => setDraftFilters(DEFAULT_FILTERS);
  const handleClearAll = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setRows([]);
    setHasFetched(false);
    setShowFilterForm(true);
    setPagination({
      total_items: 0,
      current_page: 1,
      total_pages: 1,
      items_per_page: 50,
      has_next_page: false,
      has_previous_page: false,
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">User Feedback Report</h1>
            <p className="text-sm text-gray-600 mt-1">Review TA, MIS, designer, and validator feedback in one report.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!showFilterForm && (
              <button onClick={() => setShowFilterForm(true)} className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
                Apply Filters Again
                {activeFiltersCount > 0 && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">{activeFiltersCount}</span>}
              </button>
            )}
            <button onClick={handleClearAll} className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
              Clear
            </button>
            <button
              onClick={fetchExportData}
              disabled={!hasFetched || exporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {showFilterForm && (
        <FilterForm
          filters={draftFilters}
          onChange={handleFilterChange}
          onApply={handleApplyFilters}
          onReset={handleResetDraftFilters}
        />
      )}

      {!showFilterForm && (
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {FEEDBACK_COLUMNS.map((column) => (
                  <th key={column} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={FEEDBACK_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">Loading...</td>
                </tr>
              ) : !hasFetched ? (
                <tr>
                  <td colSpan={FEEDBACK_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-500">Apply filters to load report.</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={FEEDBACK_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">No feedback records found.</td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const feedbackRow = buildFeedbackRow(row);
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      {FEEDBACK_COLUMNS.map((column) => (
                        <td key={column} className="px-4 py-3 text-sm text-gray-700 align-top max-w-sm whitespace-normal break-words">
                          {feedbackRow[column]}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {hasFetched && (
          <Pagination
            pagination={paginationAdapter}
            onPageChange={(nextPage) => fetchRows(nextPage, pagination.items_per_page, appliedFilters)}
            onLimitChange={(nextLimit) => fetchRows(1, nextLimit, appliedFilters)}
          />
        )}
      </div>
      )}

      {showFilterForm && !hasFetched && (
        <div className="bg-white border border-gray-200 rounded flex flex-col items-center justify-center py-16 text-center">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-700">Apply filters to view report.</p>
          <p className="text-xs text-gray-400 mt-1">Use the filters above and click Apply Filters.</p>
        </div>
      )}

      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="user_feedback_report"
        title="Export User Feedback Report"
        description="Download user feedback report as Excel or CSV"
      />
    </div>
  );
};

export default UserFeedbackReport;
